/**
 * Admin Users Page
 *
 * Manage super admin users with create, edit, and delete functionality.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { cn, Button, Input, useToast } from '@respectly/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@respectly/ui';
import {
  Users,
  Plus,
  Search,
  RefreshCw,
  ArrowLeft,
  X,
} from 'lucide-react';
import type {
  CreateSuperAdminUserDto,
  SuperAdminRole,
  SuperAdminUserListItem,
  UpdateSuperAdminUserDto,
} from '@respectly/shared-types';
import {
  useAdminUsersStore,
  useAdminUserFiltersCount,
} from '../../stores/useAdminUsersStore';
import {
  AdminUserCardGrid,
  AdminUserCardSkeleton,
  AdminUserModal,
  getRoleOptions,
} from '../../components/settings';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ============================================
// Component
// ============================================

export function AdminUsers() {
  const {
    users,
    isLoadingList,
    filters,
    setFilter,
    clearFilters,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    resetPassword,
    isCreating,
    isUpdating,
  } = useAdminUsersStore();

  const activeFiltersCount = useAdminUserFiltersCount();
  const roleOptions = getRoleOptions();
  const { toast } = useToast();

  // Local state
  const [searchValue, setSearchValue] = useState(filters.search ?? '');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SuperAdminUserListItem | null>(null);

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        setFilter('search', searchValue || undefined);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, filters.search, setFilter]);

  // Handle edit
  const handleEdit = (user: SuperAdminUserListItem) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  // Handle create
  const handleCreate = () => {
    setSelectedUser(null);
    setModalOpen(true);
  };

  // Handle toggle active
  const handleToggleActive = async (user: SuperAdminUserListItem) => {
    await updateUser(user.id, { isActive: !user.isActive });
  };

  // Handle delete
  const handleDelete = async (user: SuperAdminUserListItem) => {
    if (confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`)) {
      await deleteUser(user.id);
    }
  };

  // Handle reset password
  const handleResetPassword = async (user: SuperAdminUserListItem) => {
    if (confirm(`Reset password for ${user.firstName} ${user.lastName}? They will receive an email.`)) {
      await resetPassword(user.id, { sendEmail: true });
    }
  };

  // Handle form submit
  const handleSubmit = async (data: CreateSuperAdminUserDto | UpdateSuperAdminUserDto) => {
    if (selectedUser) {
      const ok = await updateUser(selectedUser.id, data as UpdateSuperAdminUserDto);
      toast(
        ok
          ? { title: 'User updated', description: `${selectedUser.firstName} ${selectedUser.lastName} has been updated.` }
          : { title: 'Update failed', description: useAdminUsersStore.getState().actionError?.message ?? 'Unable to update user.', variant: 'error' }
      );
      return ok;
    }
    const createDto = data as CreateSuperAdminUserDto;
    const newId = await createUser(createDto);
    toast(
      newId !== null
        ? { title: 'Admin user created', description: `${createDto.firstName} ${createDto.lastName} has been added.` }
        : { title: 'Create failed', description: useAdminUsersStore.getState().actionError?.message ?? 'Unable to create admin user.', variant: 'error' }
    );
    return newId !== null;
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/settings"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-100 to-violet-200 dark:from-violet-900/40 dark:to-violet-800/40 flex items-center justify-center">
              <Users className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Admin Users
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Manage super admin users and their roles
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadUsers()}
              disabled={isLoadingList}
            >
              <RefreshCw
                className={cn('w-4 h-4 mr-2', isLoadingList && 'animate-spin')}
              />
              Refresh
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mt-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search users..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={filters.role ?? 'all'}
            onValueChange={(value) =>
              setFilter('role', value === 'all' ? undefined : (value as SuperAdminRole))
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.isActive === undefined ? 'all' : String(filters.isActive)}
            onValueChange={(value) =>
              setFilter('isActive', value === 'all' ? undefined : value === 'true')
            }
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-slate-500"
            >
              <X className="w-4 h-4 mr-1" />
              Clear ({activeFiltersCount})
            </Button>
          )}
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoadingList ? (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <AdminUserCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <motion.div variants={itemVariants}>
            <AdminUserCardGrid
              users={users}
              onEdit={handleEdit}
              onResetPassword={handleResetPassword}
              onToggleActive={handleToggleActive}
              onDelete={handleDelete}
            />
          </motion.div>
        )}
      </div>

      {/* Modal */}
      <AdminUserModal
        user={selectedUser as unknown as undefined}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmit={handleSubmit}
        isLoading={isCreating || isUpdating}
      />
    </motion.div>
  );
}
